import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  TeamLeaderCoverageConfig,
  TeamLeaderCoverageRow,
  TeamLeaderOption
} from '../../domain/dc-schedule.model';
import { AlertService } from '../../services/alert.service';
import { DcRosterService } from '../../services/dc-roster.service';

export interface TeamLeaderAssignmentConfigDialogData {
  leagueOptions: string[];
  teamLeaders: TeamLeaderOption[];
}

@Component({
  selector: 'app-team-leader-assignment-config-dialog',
  templateUrl: './team-leader-assignment-config-dialog.component.html',
  styleUrls: ['./team-leader-assignment-config-dialog.component.css']
})
export class TeamLeaderAssignmentConfigDialogComponent implements OnInit {
  rows: TeamLeaderCoverageRow[] = [this.createEmptyRow()];
  config: TeamLeaderCoverageConfig | null = null;
  loading = true;
  saving = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: TeamLeaderAssignmentConfigDialogData,
    private dialogRef: MatDialogRef<
      TeamLeaderAssignmentConfigDialogComponent,
      TeamLeaderCoverageRow[] | null
    >,
    private dcRosterService: DcRosterService,
    private alertService: AlertService
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.config = await firstValueFrom(
        this.dcRosterService.getTeamLeaderCoverageConfig()
      );
      this.rows = this.config
        ? (this.config.rows ?? []).map((row) => ({ ...row }))
        : [this.createEmptyRow()];
    } catch (error) {
      this.alertService.showError(
        'Could not load Team Leader coverage: ' + this.getErrorMessage(error)
      );
    } finally {
      this.loading = false;
    }
  }

  get coverageSums(): { leagueShortName: string; coverage: number }[] {
    const sums = new Map<string, number>();
    for (const row of this.rows) {
      const leagueShortName = row.leagueShortName?.trim();
      if (!leagueShortName) {
        continue;
      }
      const coverage = Number(row.coverage);
      sums.set(
        leagueShortName,
        (sums.get(leagueShortName) ?? 0) +
          (Number.isFinite(coverage) ? coverage : 0)
      );
    }
    return [...sums.entries()].map(([leagueShortName, coverage]) => ({
      leagueShortName,
      coverage
    }));
  }

  get allValid(): boolean {
    return (
      this.rows.every(
        (row) =>
          !!row.leagueShortName?.trim() &&
          !!row.teamLeaderAuthId?.trim() &&
          Number.isFinite(Number(row.coverage)) &&
          Number.isInteger(Number(row.coverage)) &&
          Number(row.coverage) > 0
      ) && this.coverageSums.every((item) => item.coverage === 100)
    );
  }

  addRow(): void {
    this.rows = [...this.rows, this.createEmptyRow()];
  }

  removeRow(index: number): void {
    this.rows = this.rows.filter((_row, rowIndex) => rowIndex !== index);
  }

  onLeagueChanged(row: TeamLeaderCoverageRow, leagueShortName: string): void {
    row.leagueShortName = leagueShortName;
    row.leagueSihfId = null;
  }

  close(): void {
    this.dialogRef.close(null);
  }

  /**
   * Persists the validated global config and returns its canonical rows to the
   * parent flow for confirmation rendering.
   */
  async saveAndContinue(): Promise<void> {
    if (!this.allValid || this.saving) {
      return;
    }

    try {
      this.saving = true;
      const config = await firstValueFrom(
        this.dcRosterService.saveTeamLeaderCoverageConfig(this.rows)
      );
      this.dialogRef.close(config.rows);
    } catch (error) {
      this.alertService.showError(
        'Could not save Team Leader coverage: ' + this.getErrorMessage(error)
      );
    } finally {
      this.saving = false;
    }
  }

  private createEmptyRow(): TeamLeaderCoverageRow {
    return {
      leagueSihfId: null,
      leagueShortName: '',
      teamLeaderAuthId: '',
      coverage: null
    };
  }

  private getErrorMessage(error: any): string {
    const validationErrors = Array.isArray(error?.error?.errors)
      ? error.error.errors
          .filter(
            (message: unknown): message is string => typeof message === 'string'
          )
          .map((message: string) => message.trim())
          .filter((message: string) => !!message)
      : [];
    if (validationErrors.length > 0) {
      return validationErrors.join(' ');
    }
    return error?.error?.message ?? error?.message ?? 'Unknown error';
  }
}
